import { AppIcon, AppText } from "@toothfairy/shared-ui";
import AppAssets from "@toothfairy/shared-ui/AppAssets";
import AppCard from "@toothfairy/shared-ui/AppCard";
import AppStateManager from "@toothfairy/shared-ui/AppStateManager";
import AppTheme from "@toothfairy/shared-ui/AppTheme";
import AppToast from "@toothfairy/shared-ui/AppToast";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { View } from "react-native";
import { Popable } from "react-native-popable";
import { default as AppConfig } from "../../../../Constants";
import useWorkspaces from "../../../../Hooks/useWorkspaces";
import { useHistory } from "../../../../Router";
import Utility from "../../../../Utility";
import WorkspaceChip from "../../../Workspaces/Components/WorkspaceChip";
import QaPreselectionToolbar from "./SubComponents/QaPreselectionToolbar";
import ToothFairyLoadingPlaceholder from "../../../../Components/ToothFairyComponents/ToothFairyLoadingPlaceholder";
import ToothFairyModal from "../../../../Components/ToothFairyComponents/ToothFairyModal";
import { isMobileScreen } from "../../../MainViewContainer";
import QaHiringTemplate from "./SubComponents/QaHiringTemplate";
const extraDay = 24 * 60 * 60 * 1000;
const adaptModeCodeToLabel = (mode) => {
  switch (mode) {
    case "speed":
      return "Operator";
    case "accuracy":
      return "Operator";
    case "coder":
      return "Programmer";
    case "mathematician":
      return "Mathematician";
    case "chatter":
      return "Assistant";
    case "planner":
      return "Orchestrator";
    case "computer":
      return "Desktop agent";
    case "retriever":
      return "Operator";
    default:
      return "Unknown";
  }
};

// Function to generate all possible chips based on item
const generateAgentChips = (item, currentTheme, subscriptionType) => {
  let remainingSkillsString = null;
  const MAX_VISIBLE_CHIPS = 6;

  // Function to generate all possible chips based on item
  const allChips = [
    {
      condition: item?.mode != null,
      label: adaptModeCodeToLabel(item?.mode),
      icon: Utility.mapAgentTypeWithIcon(item, AppAssets),
      customStyle: {
        backgroundColor: currentTheme?.theme?.primary,
        fontColor: currentTheme?.theme?.white,
      },
    },
    {
      condition: item?.hasCode,
      label: "Code execution",
      icon: AppAssets.icons.code,
    },
    {
      condition: item?.allowInternetSearch,
      label: "Internet search",
      icon: AppAssets.icons.web,
    },
    {
      condition: item?.allowImagesGeneration,
      label: "Image creation",
      icon: AppAssets.icons.art,
    },
    {
      condtion: item?.allowVideosGeneration,
      label: "Video generation",
      icon: AppAssets.icons.video,
    },
    {
      condition: item?.allow3dModelGeneration,
      label: "3D model generation",
      icon: AppAssets.icons.model3d,
    },
    {
      condition: item?.allowDocsUpload,
      label: "Files upload",
      icon: AppAssets.icons.files,
    },
    {
      condition: item?.allowImagesUpload,
      label: "Image analysis",
      icon: AppAssets.icons.image,
    },
    {
      condition: item?.charting,
      label: "Graphs enabled",
      icon: AppAssets.icons.chartingAgent,
    },
    {
      condition: item?.allowAudiosGeneration,
      label: "Audio generation",
      icon: AppAssets.icons.audio,
    },
    {
      condition: item?.blender,
      label: "Reasoning",
      icon: AppAssets.icons.advancedReasoning,
    },
    {
      condition: item?.hasNER,
      label: "Entity recognition",
      icon: AppAssets.icons.ner,
    },
    {
      condition: item?.advancedImageGeneration,
      label: "High-fidelity",
      icon: AppAssets.icons.advancedImage,
    },
    {
      condition: item?.allowVideosUpload,
      label: "Video analysis",
      icon: AppAssets.icons.video,
    },
    {
      condition: item?.allowAudiosUpload,
      label: "Audio Analysis",
      icon: AppAssets.icons.audio,
    },
    {
      condition: item?.promptBuffering,
      label: "Prompt buffering",
      icon: AppAssets.icons.promptBuffering,
    },
    {
      condition: item?.allowDeepInternetSearch,
      label: "Deep research",
      icon: AppAssets.icons.deepSearch,
    },
    {
      condition: item?.hasModeration,
      label: "Content safety",
      icon: AppAssets.icons.shield,
    },
    {
      condition: item?.chainOfThoughts,
      label: "Tree of thoughts",
      icon: AppAssets.icons.treeOfThoughts,
    },
    {
      condition: item?.hasContentCreation,
      label: "Content creation",
      icon: AppAssets.icons.writer,
    },
    {
      condition: item?.hasEntityExtraction,
      label: "Entities extraction",
      icon: AppAssets.icons.entities,
    },
    {
      condition: item?.topK > 10,
      label: "Bulk retrieval",
      icon: AppAssets?.icons?.collection,
    },
  ];
  if (
    (subscriptionType == "base" || subscriptionType == "starter") &&
    item?.minimumSubscriptionType == "pro"
  )
    allChips.splice(1, 0, {
      condition: true,
      label: "Pro only",
      icon: AppAssets.icons.subscriptionType,
      customStyle: {
        backgroundColor: currentTheme?.theme?.orange,
        fontColor: currentTheme?.theme?.white,
      },
    });
  if (
    subscriptionType != "enterprise" &&
    item?.minimumSubscriptionType == "enterprise"
  )
    allChips.splice(1, 0, {
      condition: true,
      label: "Enterprise only",
      icon: AppAssets.icons.subscriptionType,
      customStyle: {
        backgroundColor: currentTheme?.theme?.orange,
        fontColor: currentTheme?.theme?.white,
      },
    });
  // Filter chips that should be displayed based on their conditions
  const visibleChips = allChips.filter((chip) => chip.condition);

  // If we have more than MAX_VISIBLE_CHIPS, slice and add the "more" chip
  if (visibleChips.length > MAX_VISIBLE_CHIPS) {
    const displayedChips = visibleChips.slice(0, MAX_VISIBLE_CHIPS);
    const remainingCount = visibleChips.length - MAX_VISIBLE_CHIPS;
    const remainingChips = visibleChips.slice(MAX_VISIBLE_CHIPS);
    remainingSkillsString = remainingChips.map((chip) => chip.label).join(", ");

    displayedChips.push({
      label: `${remainingCount} more skills`,
      icon: AppAssets.icons.more,
      isPlusOnly: true,
      remainingSkillsString,
    });

    return displayedChips;
  } else {
    return visibleChips;
  }
};

const QaPreSelection = ({
  agents,
  handleCreation,
  setShowPreselectionModal,
}) => {
  const { currentTheme } = AppTheme.useTheme();
  const history = useHistory();
  const { state, dispatch } = AppStateManager.useAppStateManager();
  const { getActiveWorkspace } = useWorkspaces();
  const [labelText, setLabelText] = React.useState("");
  const [valueText, setValueText] = React.useState("");
  const [agentType, setAgentType] = React.useState(null);
  const [agentDepartment, setAgentDepartment] = React.useState(null);
  const [filteredAgents, setFilteredAgents] = React.useState(agents);
  const [isLoading, setIsLoading] = React.useState(true);
  const { t } = useTranslation();
  const AppToasty = AppToast.useToast();
  const [agentHiringTemplate, setAgentHiringTemplate] = useState(null);
  const [isHiringTemplateModalOpen, setIsHiringTemplateModalOpen] =
    React.useState(false);
  const openHiringModal = (item) => {
    setIsHiringTemplateModalOpen(true);
    setAgentHiringTemplate(item);
  };
  useEffect(() => {
    handleSearch();
  }, [agents?.length]);
  const handleSearch = () => {
    try {
      setIsLoading(true);
      setFilteredAgents(
        agents
          ?.filter((agent) => {
            return (
              agent.label.toLowerCase().includes(labelText.toLowerCase()) &&
              agent.description
                .toLowerCase()
                .includes(valueText.toLowerCase()) &&
              (agent.mode?.toLowerCase() == agentType ||
                agentType == null ||
                agentType == "[object Object]") &&
              (agent.department == agentDepartment ||
                agentDepartment == null ||
                agentDepartment == "[object Object]")
            );
          })
          ?.sort((a, b) => {
            if (a.label < b.label) {
              return -1;
            }
            if (a.label > b.label) {
              return 1;
            }
            return 0;
          })
      );
    } catch (error) {
      console.log(error);
    } finally {
      setTimeout(() => {
        setIsLoading(false);
      }, 500);
    }
  };
  useEffect(() => {
    handleSearch();
  }, [labelText, valueText, agentType, agentDepartment]);
  const hiringTemplateModal = useMemo(() => {
    return (
      <ToothFairyModal
        modalStyle={{
          height: "auto",
          minHeight: 250,
          maxWidth: 600,
          width: isMobileScreen() ? "99%" : "90%",
          marginHorizontal: "auto",
          marginTop: "auto",
          marginBottom: "auto",
          margin: "auto",
        }}
        key={"hiring_selection"}
        isModalOpen={isHiringTemplateModalOpen}
        setIsModalOpen={setIsHiringTemplateModalOpen}
        modalTitle={`Agent hiring wizard`}
        closeModal={() => {
          setIsHiringTemplateModalOpen(false);
          setAgentHiringTemplate(null);
        }}
      >
        <QaHiringTemplate
          item={agentHiringTemplate}
          setIsHiringTemplateModalOpen={setIsHiringTemplateModalOpen}
          setShowPreselectionModal={setShowPreselectionModal}
        />
      </ToothFairyModal>
    );
  }, [
    isHiringTemplateModalOpen,
    JSON.stringify(agentHiringTemplate),
    setShowPreselectionModal,
  ]);
  return (
    <View
      style={{
        zIndex: -1,
        flex: 1,
        marginRight: -5,
        marginLeft: -10,
        overflowY: "auto",
        overflowX: "hidden",
      }}
    >
      {isLoading ? (
        <ToothFairyLoadingPlaceholder caption="Loading hireable agents..." />
      ) : (
        <AppToast.ToastProvider
          {...AppConfig.notificationConfig}
          // offset for both top and bottom toasts
        >
          <QaPreselectionToolbar
            handleSearch={handleSearch}
            labelText={labelText}
            setLabelText={setLabelText}
            valueText={valueText}
            setValueText={setValueText}
            agentType={agentType}
            setAgentType={setAgentType}
            agentDepartment={agentDepartment}
            setAgentDepartment={setAgentDepartment}
          />
          <View
            style={{
              flexWrap: "wrap",
              justifyContent: "flex-start",
              flexDirection: "row",
            }}
            contentContainerStyle={{
              padding: 10,
            }}
          >
            <AppCard
              isRightIconPopupDisplayed={true}
              rightIconPopupContent={"Create a new agent from scratch"}
              rightIconDisplayed
              rightIconName={AppAssets.icons.plus}
              rightIconSize={14}
              rightIconColor={currentTheme?.theme?.primary_color}
              rightIconStyle={{
                marginTop: 3,
              }}
              isHoverable
              accessibilityLabel={`agent-card-new`}
              key={"new-agent"}
              headerContainerStyle={{
                alignItems: "center",
              }}
              style={{
                marginHorizontal: 20,
                borderColor: currentTheme?.theme?.app_outline_color,
              }}
              onCardPress={() => {
                // if (isWorkspaceDisabled(item)) history.push("/profile/billing");
                handleCreation("new", "New agent");
              }}
              //   disabled={isWorkspaceInCreation(item)}
              cardWidth={300}
              cardHeight={260}
              heading={"New Agent"}
              shadowColor={currentTheme?.theme?.transparent}
              footerHeadingStyle={{
                fontSize: currentTheme.theme.small_font_size,
                fontWeight: "normal",
                paddingBottom: 10,
                maxWidth: 170,
              }}
              // rightIconOnPress={() => {
              //   user.id === item?.userID && handleWorkspaceEdit(item);
              // }}
              headingStyle={{
                maxWidth: 240,
                fontSize: 18,
                marginTop: -3,
              }}
              subFooterHeadingStyle={{
                maxWidth: 170,
              }}
              childrenViewStyle={{
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <AppIcon icon={AppAssets.icons.taskAgent} size={50} />
              <AppText
                wrapperStyle={{
                  marginTop: 10,
                }}
                style={{
                  fontWeight: "bold",
                }}
              >
                Start from scratch
              </AppText>
            </AppCard>
            {filteredAgents?.map((item) => (
              <div className="select_agent">
                <AppCard
                  rightIconPopupContent={item?.description}
                  leftIconName={AppAssets.icons.taskAgent}
                  isHoverable
                  accessibilityLabel={`agent-card-${item.id}`}
                  key={item.id}
                  headerContainerStyle={{
                    alignItems: "flex-start",
                  }}
                  style={{
                    marginHorizontal: 20,
                    borderColor: currentTheme?.theme?.app_outline_color,
                    zIndex: -1,
                  }}
                  onCardPress={() => {
                    subscriptionType = getActiveWorkspace()?.subscriptionType;
                    if (
                      ((subscriptionType == "base" ||
                        subscriptionType == "starter") &&
                        item?.minimumSubscriptionType == "pro") ||
                      (subscriptionType != "enterprise" &&
                        item?.minimumSubscriptionType == "enterprise")
                    ) {
                      return AppToasty.show(
                        t(
                          "agentCannotBeRecruitedDueToSubscriptionErrorCaption"
                        ),
                        {
                          type: "danger",
                        }
                      );
                      // CHANGE WHEN IMPLEMENETED
                    } else if (item?.agentHiringTemplate) {
                      openHiringModal({
                        ...item,
                        agentHiringTemplate:
                          typeof item?.agentHiringTemplate == "string"
                            ? JSON.parse(item?.agentHiringTemplate)
                            : item?.agentHiringTemplate,
                      });
                    } else {
                      handleCreation(item?.id, item?.label);
                    }
                  }}
                  //   disabled={isWorkspaceInCreation(item)}
                  cardWidth={300}
                  cardHeight={260}
                  heading={item?.label}
                  shadowColor={currentTheme?.theme?.transparent}
                  footerHeadingStyle={{
                    fontSize: currentTheme.theme.small_font_size,
                    fontWeight: "normal",
                    paddingBottom: 10,
                    maxWidth: 170,
                  }}
                  // rightIconOnPress={() => {
                  //   user.id === item?.userID && handleWorkspaceEdit(item);
                  // }}
                  headingStyle={{
                    maxWidth: 240,
                    fontSize: 18,
                    marginBottom: 5,
                    marginTop: -3,
                  }}
                  subFooterHeadingStyle={{
                    maxWidth: 170,
                  }}
                  rightIconSize={14}
                  rightIconName={AppAssets?.icons?.info}
                  rightIconDisplayed
                  isRightIconClickable={
                    item?.disabled !== true &&
                    item?.activeUntil &&
                    new Date(item.activeUntil).getTime() + extraDay > Date.now()
                  }
                  // rightIconName={
                  //   user.id === item?.userID && AppAssets.icons.info
                  // }
                  childrenViewStyle={{
                    justifyContent: "flex-end",
                    zIndex: -1,
                  }}
                  subHeading={item?.department
                    ?.toLowerCase()
                    .replace(/_/g, " ")
                    .replace(/\b\w/g, (c) => c.toUpperCase())}
                  subHeadingStyle={{
                    fontSize: currentTheme?.theme?.extra_small_font_size,
                    marginLeft: 10,
                    fontWeight: "bold",
                  }}
                  subHeadingColor={currentTheme?.theme?.orange}
                  subHeadingIcon={
                    item?.department
                      ? Utility.mapDepartmentWithIcon(
                          item?.department,
                          AppAssets
                        )
                      : null
                  }
                  subHeadingIconSize={15}
                  subHeadingIconColor={currentTheme?.theme?.orange}
                >
                  {/* {item?.department && (
                <WorkspaceChip
                  key={"department"}
                  label={item?.department
                    ?.toLowerCase()
                    .replace(/_/g, " ")
                    .replace(/\b\w/g, (c) => c.toUpperCase())}
                  leftIcon={Utility.mapDepartmentWithIcon(item, AppAssets)}
                  customStyle={{
                    backgroundColor: currentTheme?.theme?.transparent,
                    fontColor: currentTheme?.theme?.white,
                  }}
                  styleContainer={{
                    marginTop: 5,
                    justifyContent: "flex-start",
                    marginLeft: 0,
                  }}
                />
              )} */}

                  <View
                    style={{
                      flexDirection: "column",
                      justifyContent: "flex-end",
                      flexWrap: "wrap",
                      height: 170,
                      zIndex: -1,
                    }}
                  >
                    {generateAgentChips(
                      item,
                      currentTheme,
                      getActiveWorkspace()?.subscriptionType
                    )?.map((chip, index) =>
                      chip?.isPlusOnly ? (
                        <Popable
                          numberOfLines={10}
                          backgroundColor={
                            currentTheme?.theme?.tooltipBackgroundColor
                          }
                          style={[
                            {
                              top: 30,
                              zIndex: 20,
                              maxWidth: 200,
                              width: 200,
                            },
                          ]}
                          content={chip?.remainingSkillsString}
                          action="hover"
                          position="bottom"
                        >
                          <WorkspaceChip
                            key={index}
                            label={chip.label}
                            leftIcon={chip.icon}
                            {...(chip.customStyle && {
                              backgroundColor: chip.customStyle.backgroundColor,
                              fontColor: chip.customStyle.fontColor,
                            })}
                          />
                        </Popable>
                      ) : (
                        <WorkspaceChip
                          key={index}
                          label={chip.label}
                          leftIcon={chip.icon}
                          {...(chip.customStyle && {
                            backgroundColor: chip.customStyle.backgroundColor,
                            fontColor: chip.customStyle.fontColor,
                          })}
                        />
                      )
                    )}
                  </View>
                </AppCard>
              </div>
            ))}
          </View>
        </AppToast.ToastProvider>
      )}
      {hiringTemplateModal}
    </View>
  );
};

export default QaPreSelection;
